import React, { ClipboardEvent, useContext } from "react";
import {
    getLongTermSeason,
    getVerticalHeaders, getVerticalHeadersForHistorical,
} from "../SeasonToMonth/header.utils";
import {
  BACKGROUND_GRAY,
  RED_50,
  TABLE_BORDER_GRAY,
  YELLOW_30,
} from "../../../constants/css.constants";
import SeasonToMonthShipmentTable from "../SeasonToMonth/SeasonToMonthShipmentTable/SeasonToMonthShipmentTable";
import { QUARTERS } from "../FuturesTable/utils/header.utils";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { COPY_PASTE_SPLIT } from "../../../constants/app.constants";
import { useParams } from "react-router";
import { TOTAL_UNITS_ACCESSOR } from "./ShipmentMonthTables.constants";
import {
  calculateQuarterlyValues,
  formatNumbersWithCommas,
} from "../../../constants/utils";
import { SeasonToMonthContext } from "../../../contexts/SeasonToMonthContext";
import { ShipmentHeaderContext } from "../../../contexts/ShipmentHeaderContext";
import { DueDateContext } from "../../../contexts/DueDateContext";
import useEditRights from "../../../hooks/useEditRights";

const ShipmentMonthTables = () => {
  const { tab } = useParams<{ tab: string }>();
  const { canEdit } = useEditRights();
  const { cupData, setCupData, setSeasonToMonthData, seasonToMonthData } =
    useContext(SeasonToMonthContext);

  const { horizontalHeaders, monthsInFirstTwoSeasons } = useContext(
    ShipmentHeaderContext,
  );
  const longTermHeaders = horizontalHeaders.MONTHS.slice(
    monthsInFirstTwoSeasons,
  );
  const seasonByMonthHeaders = longTermHeaders.map(str => str.slice(0, -3));
  const { shipmentMonthDate } = useContext(DueDateContext);
  const { verticalHeadersForHistorical } = getVerticalHeadersForHistorical(shipmentMonthDate);
  const { verticalHeaders } = getVerticalHeaders(shipmentMonthDate);
  const LONG_TERM_SEASON = getLongTermSeason(shipmentMonthDate);
  const LONG_TERM_SEASON_START_MONTH = LONG_TERM_SEASON * 3;

  const updateData = (row: number, column: number) => (value: string) => {
    const rowToUpdate = [...seasonToMonthData[row]];
    const newRow = [
      ...rowToUpdate.slice(0, column),
      parseFloat(value),
      ...rowToUpdate.slice(column + 1),
    ];

    // Update Monthly Total and Quarters
    const slicedRow = newRow.slice(0, TOTAL_UNITS_ACCESSOR);
    const total = slicedRow.reduce((acc, val) => {
      const number = isNaN(val) ? 0 : val;
      return acc + number;
    }, 0);
    const quarterlyValues = calculateQuarterlyValues(
      slicedRow,
      LONG_TERM_SEASON_START_MONTH,
    );
    const updatedRow = slicedRow.concat(total).concat(quarterlyValues);
    const updatedData = [
      ...seasonToMonthData.slice(0, row),
      updatedRow,
      ...seasonToMonthData.slice(row + 1),
    ];
    updateSeasonToMonth(row, column, value);
    setSeasonToMonthData(updatedData);
  };

  const updateSeasonToMonth = (row: number, column: number, value: string) => {
    const rowToUpdate = [...cupData[row]];
    const totalValue = rowToUpdate[TOTAL_UNITS_ACCESSOR + 1];
    const newValue = (parseFloat(value) / 100) * totalValue;
    const difference = newValue - rowToUpdate[column];
    const newRow = [
      ...rowToUpdate.slice(0, column),
      newValue,
      ...rowToUpdate.slice(column + 1),
    ];
    newRow[newRow.length - 3] += difference;
    newRow[newRow.length - 1] =
      newRow[newRow.length - 3] - newRow[newRow.length - 2];
    const updatedData = [
      ...cupData.slice(0, row),
      newRow,
      ...cupData.slice(row + 1, cupData.length - 1),
      [
        ...cupData[cupData.length - 1].slice(0, column),
        cupData[cupData.length - 1][column] + difference,
        ...cupData[cupData.length - 1].slice(column + 1),
      ],
    ];

    setCupData(updatedData);
  };

  const seasonToQuarterTransformation = (value) => {
    return value === 0 ? "0%" : `${value.toFixed(2)}%`;
  };

  const isTB = tab === "tb";
  const optionalStyles = (value: number, j) => {
    const n = Math.floor(value);
    if (isTB || !canEdit) {
      return { backgroundColor: BACKGROUND_GRAY, cursor: "default" };
    }
    return n < 0 || n > 100 || (n > 90 && n < 100)
      ? { backgroundColor: RED_50 }
      : n < 91 && n !== 0 && j === TOTAL_UNITS_ACCESSOR
        ? { backgroundColor: YELLOW_30, color: "#000" }
        : j > TOTAL_UNITS_ACCESSOR
          ? {
              width: "75px",
              maxWidth: "75px",
              backgroundColor: BACKGROUND_GRAY,
              cursor: "default",
            }
          : j < TOTAL_UNITS_ACCESSOR
            ? {
                backgroundColor: "#fff",
                color: "#000",
                minWidth: "110px",
              }
            : { backgroundColor: BACKGROUND_GRAY, cursor: "default" };
  };

  const onPaste =
    (row: number, column: number) =>
    (e: ClipboardEvent<HTMLTableCellElement>) => {
      const data = e.clipboardData.getData("text");
      const splitData = data
        .split(COPY_PASTE_SPLIT)
        .filter((datum) => datum !== "")
        .map((datum) =>
          datum
            .split(/\W/)
            .map((subStr) => parseFloat(subStr.replaceAll(",", ""))),
        );
      const dataToUpdate = seasonToMonthData.slice(row, row + splitData.length);
      const incomingDataDifferences = splitData.map((dataRow, i) =>
        dataRow.map(
          (value, j) => value - seasonToMonthData[row + i][column + j],
        ),
      );
      const verticalDifferences = incomingDataDifferences.reduce(
        (acc, value) => {
          const reducedValue = value.reduce(
            (innerAcc, curr) => innerAcc + curr,
            0,
          );
          return acc.concat([reducedValue]);
        },
        [],
      );
      const updatedData = dataToUpdate.map((datum, i) => {
        const baseData = datum
          .slice(0, column)
          .concat(splitData[i])
          .concat(
            datum.slice(column + splitData[i].length, TOTAL_UNITS_ACCESSOR),
          );
        const quarterlyValues = calculateQuarterlyValues(
          baseData,
          LONG_TERM_SEASON_START_MONTH,
        );

        return baseData
          .concat(verticalDifferences[i] + datum[TOTAL_UNITS_ACCESSOR])
          .concat(quarterlyValues);
      });
      const finalShipmentData = seasonToMonthData
        .slice(0, row)
        .concat(updatedData)
        .concat(seasonToMonthData.slice(row + splitData.length));

      const cupDataToUpdate = cupData.slice(row, row + splitData.length);
      const incomingCupData = splitData.map((row, i) =>
        row.map(
          (column) => (column / 100) * cupData[i][TOTAL_UNITS_ACCESSOR + 1],
        ),
      );
      const updatedCupData = cupDataToUpdate.map((datum, i) => {
        const updatedCupDatum = [
          ...datum.slice(0, column),
          ...incomingCupData[i],
          ...datum.slice(column + splitData[i].length),
        ];
        const newTotal = updatedCupDatum
          .slice(0, updatedCupDatum.length - 3)
          .reduce((acc, value) => acc + value, 0);
        updatedCupDatum[updatedCupDatum.length - 3] = newTotal;
        updatedCupDatum[updatedCupDatum.length - 1] =
          newTotal - updatedCupDatum[updatedCupDatum.length - 2];
        return updatedCupDatum;
      });

      const differences = incomingCupData
        .map((dataRow, i) =>
          dataRow.map((value, j) => value - cupData[row + i][column + j]),
        )
        .reduce((acc, value) => {
          if (!acc.length) {
            return value;
          }
          return acc.map((val, i) => val + value[i]);
        }, []);

      const totalsRow = cupData[cupData.length - 1];
      const newTotals = differences.map(
        (total, i) => total + totalsRow[i + column],
      );
      const finalCupData = [
        ...cupData.slice(0, row),
        ...updatedCupData,
        ...cupData.slice(row + splitData.length, cupData.length - 1),
        [
          ...totalsRow.slice(0, column),
          ...newTotals,
          ...totalsRow.slice(column + newTotals.length),
        ],
      ];
      setCupData(finalCupData);
      setSeasonToMonthData(finalShipmentData);
    };

  return (
    <>
      <Box
        display="flex"
        flexDirection="column"
        alignItems="flex-start"
        marginLeft="50px"
        marginBottom="5px"
        marginTop="-30px"
      >
        <Box
          display="flex"
          flexDirection={"column"}
          alignItems="flex-end"
          maxWidth="1800px"
          marginLeft="125px"
        >
          <Box display="flex">
            <Typography variant="button" marginRight="400px">
              Shipment Month
            </Typography>
            <Box
              border={TABLE_BORDER_GRAY}
              display="flex"
              justifyContent="center"
              alignItems="center"
              style={{ width: "401px", backgroundColor: "#fff", color: "#000" }}
            >
              Season To Quarter
            </Box>
          </Box>
          <Box display="flex">
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              width="55px"
              maxWidth="55px"
              height="130px"
              maxHeight="130px"
              border={TABLE_BORDER_GRAY}
              padding="0 20px"
              style={{
                backgroundColor: "#fff",
                color: "#000",
              }}
            >
              <Typography
                variant="subtitle2"
                style={{
                  writingMode: "vertical-lr",
                  transform: "rotate(180deg)",
                }}
              >
                Season To Month
              </Typography>
              <Typography
                variant="caption"
                style={{
                  writingMode: "vertical-lr",
                  transform: "rotate(180deg)",
                  fontSize: 9,
                }}
              >
                Historical Shipment % (3 yr)
              </Typography>
            </Box>
            <SeasonToMonthShipmentTable
              additionalHeaders={["Monthly Total", ...QUARTERS]}
              horizontalHeaders={seasonByMonthHeaders}
              tab={tab.toUpperCase()}
              verticalHeaders={verticalHeadersForHistorical}
              data={seasonToMonthData}
              transform={seasonToQuarterTransformation}
              updateData={updateData}
              optionalStyles={optionalStyles}
              onPaste={onPaste}
              disabled={!canEdit}
            />
          </Box>
        </Box>
        <Box marginTop="20px" marginBottom="20px" marginLeft="-51px">
          <Box display="flex">
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              border={TABLE_BORDER_GRAY}
              padding="0 20px"
              width="55px"
              minWidth="55px"
              height="154px"
              maxHeight="154px"
              style={{
                backgroundColor: "#fff",
                color: "#000",
              }}
            >
              <Typography
                variant="subtitle2"
                style={{
                  writingMode: "vertical-lr",
                  transform: "rotate(180deg)",
                }}
              >
                Planning
              </Typography>
                <Typography
                    variant="caption"
                    style={{
                        writingMode: "vertical-lr",
                        transform: "rotate(180deg)",
                        fontSize: 8,
                    }}
                >
                    Historical Shipment % applied to Plan
                </Typography>
            </Box>
            <SeasonToMonthShipmentTable
              additionalHeaders={["Total", "Plan", "Var"]}
              horizontalHeaders={seasonByMonthHeaders}
              verticalHeaders={[...verticalHeaders, "LR Total"]}
              data={cupData}
              optionalStyles={() => ({
                backgroundColor: BACKGROUND_GRAY,
                minWidth: "110px",
                cursor: "default",
              })}
              tab={tab.toUpperCase()}
              transform={(data) =>
                formatNumbersWithCommas(Math.floor(Math.round(data)))
              }
              updateData={updateData}
              disabled
            />
          </Box>
        </Box>
      </Box>
    </>
  );
};
export default ShipmentMonthTables;
